/* One simulation, agentic or manual - the same page for both.
 *
 * Read in the order a planner decides: what it does to the DOH and which
 * working days it moves - at a glance, one strip per crew - then the curve
 * and the grid. The actions are the life of the simulation: apply it (the
 * family is then shown with it, everywhere), edit and re-run it, download
 * it, delete it.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, type SimulationDetail as Detail, type StreamSummary } from '../api/client'
import { SimulationForm } from '../components/SimulationForm'
import { PlanGrid } from '../components/PlanGrid'
import { dohScale } from '../components/dohScale'
import { Applied, IconButton, ModeChip } from '../components/icons'
import { describeOptions, round, when } from '../components/simulation'

interface Props {
  id: number
  streams: StreamSummary[]
  canEdit: boolean
  canDownload: boolean
  onBack: () => void
  onOpen: (id: number) => void
  /* The plan or the list changed: refresh what shows them. */
  onChanged: () => void
}

const MASTER_LABELS: Record<string, string> = {
  min_doh: 'MIN DOH',
  max_doh: 'MAX DOH',
  mpw: 'MPW',
  wsd_minutes: 'WSD',
  standard_price: 'Standard price',
}

export function SimulationDetail({ id, streams, canEdit, canDownload, onBack, onOpen, onChanged }: Props) {
  const [sim, setSim] = useState<Detail | null>(null)
  /* The simulation its family is shown with; null = the plan. */
  const [chosen, setChosen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)

  const load = useCallback(async () => {
    try {
      const detail = await api.simulation(id)
      setSim(detail)
      const list = await api.simulations(detail.value_stream)
      setChosen(list.chosen[detail.value_stream] ?? null)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [id])

  useEffect(() => {
    setSim(null)
    void load()
  }, [load])

  const act = async (what: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await what()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  /* Applying = the family is shown with this simulation: the dashboard, the
     charts and the reports all follow, and its status reads "applied". */
  const choose = (simulationId: number | null) =>
    act(async () => {
      if (!sim) return
      await api.chooseSimulation(sim.value_stream, simulationId)
      setChosen(simulationId)
      onChanged()
    })

  const remove = () => {
    if (!sim || !window.confirm(`Delete "${sim.name}"? This cannot be undone.`)) return
    void act(async () => {
      await api.deleteSimulation(id)
      onChanged()
      onBack()
    })
  }

  if (error && !sim) return <div className="card" style={{ padding: 20 }}>{error}</div>
  if (!sim) return <div className="card" style={{ padding: 20 }}>Loading…</div>

  const applied = chosen === sim.id
  const overridden = Object.entries(sim.master_data.simulated).filter(
    ([k, v]) => v !== sim.master_data.stored[k as keyof typeof sim.master_data.stored],
  )
  const crewOverrides = Object.entries(sim.overrides.crews ?? {})

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <header className="card page-head">
        <div className="page-title">
          <button className="link small" onClick={onBack} style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
            ← Dashboard
          </button>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {sim.name} <ModeChip mode={sim.mode} /> <Applied on={applied} />
          </h1>
          <div className="page-sub">
            {sim.value_stream} · {describeOptions(sim)} · {when(sim.created_at)} by {sim.created_by}
          </div>
        </div>
        <div className="page-actions">
          {canEdit &&
            (applied ? (
              <button disabled={busy} onClick={() => void choose(null)} title="Show the family with its current plan again">
                Unapply
              </button>
            ) : (
              <button className="primary" disabled={busy} onClick={() => void choose(sim.id)}
                title="Show the family with this simulation: dashboard, charts and reports follow">
                Apply
              </button>
            ))}
          <div className="toolbar">
            {canEdit && <IconButton icon="edit" title="Edit" disabled={busy} onClick={() => setEditing(true)} />}
            {canDownload && (
              <IconButton icon="download" title="Download Excel" disabled={busy} onClick={() => void act(() => api.downloadSimulation(id))} />
            )}
            {canEdit && <IconButton icon="trash" title="Delete" danger disabled={busy} onClick={remove} />}
          </div>
        </div>
      </header>

      {error && (
        <div className="banner-error">
          <strong>⚠ Refused.</strong> {error}
        </div>
      )}

      <Impact sim={sim} />

      <DohChart sim={sim} />

      {/* Master data, only where it differs from the stored reference. */}
      {(overridden.length > 0 || crewOverrides.length > 0) && (
        <section className="card" style={{ padding: '12px 16px', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <strong className="small">Master data changed</strong>
          {overridden.map(([k, v]) => (
            <span key={k} className="small mono">
              {MASTER_LABELS[k] ?? k}{' '}
              <span className="muted">{round(sim.master_data.stored[k as keyof typeof sim.master_data.stored])}</span> →{' '}
              <strong>{round(v)}</strong>
            </span>
          ))}
          {crewOverrides.map(([code, fields]) => (
            <span key={code} className="small">
              {sim.crews.find((c) => c.code === code)?.name ?? code}:{' '}
              {Object.entries(fields)
                .map(([f, v]) => `${f.replace('_', ' ')} ${typeof v === 'boolean' ? (v ? 'on' : 'off') : v}`)
                .join(' · ')}
            </span>
          ))}
        </section>
      )}

      <PlanGrid
        grid={sim.grid}
        changed={new Map(sim.changes.map((c) => [`${c.crew_index}:${c.bucket_index}`, c.before]))}
        before={{ doh: sim.series.before.doh, status: sim.series.before.status }}
      />

      {editing && (
        <SimulationForm
          streams={streams}
          editing={sim}
          onClose={() => setEditing(false)}
          onOpen={onOpen}
          onSaved={() => {
            void load()
            onChanged()
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------- impact */

/* The result and what moved, in one card: the deltas on top, then one strip
 * per crew with a square per bucket - grey where the working days are the
 * plan's, coloured where the simulation changed them. What changed and what
 * did not is read in one look; hovering a square gives the figures. */
function Impact({ sim }: { sim: Detail }) {
  const k = sim.kpis
  const byCell = new Map(sim.changes.map((c) => [`${c.crew_index}:${c.bucket_index}`, c]))
  const total = sim.crews.length * sim.buckets.length
  const untouched = sim.crews.filter((c) => !sim.changes.some((x) => x.crew_index === c.index)).length

  return (
    <section className="card" style={{ padding: 16, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
        <Delta label="weeks in band" before={k.before.weeks_in_band} after={k.after.weeks_in_band} higherIsBetter />
        <Delta label="weeks short" before={k.before.weeks_below_min} after={k.after.weeks_below_min} />
        <Delta label="weeks in excess" before={k.before.weeks_above_max} after={k.after.weeks_above_max} />
        <Delta label="overtime days" before={k.before.overtime_days} after={k.after.overtime_days} />
        <Delta label="average DOH" before={k.before.average_doh} after={k.after.average_doh} neutral />
      </div>

      <div className="change-map">
        {sim.crews.map((c) => {
          const own = sim.changes.filter((x) => x.crew_index === c.index).length
          return (
            <div key={c.code} className="change-row">
              <span className="mono" title={c.name}>{c.name}</span>
              <div className="change-cells">
                {sim.buckets.map((b, t) => {
                  const cell = byCell.get(`${c.index}:${t}`)
                  return (
                    <span
                      key={t}
                      className={cell ? (cell.after < cell.before ? 'less' : 'more') : ''}
                      title={cell ? `${b.label}: ${round(cell.before)} → ${round(cell.after)} days` : `${b.label}: unchanged`}
                    />
                  )
                })}
              </div>
              <span className="muted" style={{ minWidth: 70, textAlign: 'right' }}>
                {own ? `${own} changed` : 'unchanged'}
              </span>
            </div>
          )
        })}
      </div>

      <div className="change-legend">
        <span><i style={{ background: 'var(--surface-3)', border: '1px solid var(--border-strong)' }} />unchanged</span>
        <span><i style={{ background: 'var(--status-warning)' }} />fewer days</span>
        <span><i style={{ background: 'var(--series-1)' }} />more days</span>
        <span style={{ marginLeft: 'auto' }}>
          <strong>{sim.changes.length}</strong> of {total} cells changed
          {untouched > 0 && ` · ${untouched} crew${untouched > 1 ? 's' : ''} untouched`}
        </span>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------ chart */

function DohChart({ sim }: { sim: Detail }) {
  const low = sim.master_data.simulated.min_doh
  const high = sim.master_data.simulated.max_doh
  const data = sim.buckets.map((b, t) => ({
    label: b.label,
    Before: sim.series.before.doh[t],
    After: sim.series.after.doh[t],
  }))
  const scale = dohScale([...sim.series.before.doh, ...sim.series.after.doh], low, high)
  return (
    <section className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <h2>Days on hand</h2>
        <span className="small muted">corridor {low}–{high} days</span>
      </div>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <ComposedChart data={data}>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <ReferenceArea y1={low} y2={high} fill="var(--status-good)" fillOpacity={0.12} stroke="var(--status-good)" strokeOpacity={0.35} />
            <XAxis dataKey="label" stroke="var(--border-strong)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickLine={false} interval={3} />
            <YAxis stroke="var(--border-strong)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickLine={false} width={44}
              domain={scale.domain} ticks={scale.ticks} allowDataOverflow />
            <Tooltip
              contentStyle={{ background: 'var(--surface-1)', border: '1px solid var(--border-strong)', borderRadius: 8, fontSize: 12 }}
              formatter={(v: number) => v.toFixed(1)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
            <Line dataKey="Before" stroke="var(--text-muted)" strokeDasharray="4 3" strokeWidth={1.5} dot={false} />
            <Line dataKey="After" stroke="var(--series-1)" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------ bits */

function Delta({
  label,
  before,
  after,
  higherIsBetter,
  neutral,
}: {
  label: string
  before: number
  after: number
  higherIsBetter?: boolean
  neutral?: boolean
}) {
  const moved = after - before
  const better = higherIsBetter ? moved > 0 : moved < 0
  const colour =
    neutral || moved === 0 ? 'var(--text-secondary)' : better ? 'var(--status-good)' : 'var(--status-critical)'
  return (
    <div>
      <div className="small muted" style={{ fontSize: 11 }}>{label}</div>
      <div className="mono" style={{ fontSize: 15 }}>
        <span className="muted">{round(before)}</span>
        <span className="muted" aria-hidden="true"> → </span>
        <strong>{round(after)}</strong>
        {moved !== 0 && (
          <span style={{ color: colour, marginLeft: 6, fontSize: 12 }}>
            {moved > 0 ? '+' : ''}
            {round(moved)}
          </span>
        )}
      </div>
    </div>
  )
}
